import { regelsVoor, volgendeStap } from './keten'
import type { Expense, IkAntwoord } from './types'
import { standVan } from './werklijst'
import {
  geenFactuur, inDispuut, magZien, pastBijOverzicht, teBoekenVoorMij, wachtOpMij, zichtVan, type Zicht,
} from './zicht.ts'

/* ------------------------------------------------------------------ *
 *  De To-do: wat TW10 Online "To-do" noemt, in bakken
 *
 *  Casper: "Je moet er natuurlijk wel voor zorgen dat ik bij die todo,
 *  enkel de lijst krijg waar ik ook echt verantwoordelijk voor ben, anders
 *  krijgen we daar weer geneus mee." En op 23 okt 2026: "echt hetgene wat
 *  desktop ook heeft, moet je ook doen".
 *
 *  Sinds die dag is de regel die van TW10 Online (zicht.ts, een letterlijke
 *  kopie van wat de functie tw10mobile gebruikt):
 *
 *  Van mij        wachtOpMij, en het is een handtekening (1ste of 2de, of
 *                 de afronding) op jouw naam. Dit is het getal op de tegel.
 *  Valideren      wachtOpMij, en het is valideren (of lezen/vastgelopen):
 *                 met tw10.valideren alle bedrijven, met het vinkje
 *                 Valideren in de matrix alleen die bedrijven (0241).
 *                 Samen met Van mij precies de To-do van TW10 Online.
 *  Exact          met Alles zien alles wat naar Exact moet of geweigerd
 *                 werd; anders alleen met het vinkje Boeken, van die
 *                 bedrijven ("Te boeken (mijn bedrijven)").
 *  Zonder naam    een handtekening zonder iemand erbij -- alleen met Alles
 *                 zien, zoals het overzicht To-do daar.
 *  In dispuut     staat stil (0240) en dus in geen van de bakken hierboven;
 *                 wel hier, voor wie hem mag zien.
 *
 *  Wat geen factuur is (een plaatje uit een mailhandtekening) staat nergens;
 *  de functie stuurt het niet eens mee.
 * ------------------------------------------------------------------ */

export interface Bakken {
  mij: Expense[]
  valideren: Expense[]
  exact: Expense[]
  zonderNaam: Expense[]
  dispuut: Expense[]
}

const nieuwsteEerst = (a: Expense, b: Expense) => (b.date - a.date) || a.id.localeCompare(b.id)

const VALIDEER_STANDEN = ['lezen', 'vastgelopen', 'aanvullen', 'valideren']

/**
 * Wat ik zie, zoals de functie het meegaf. Een functie van vóór 23 okt 2026
 * stuurt nog geen zicht mee: dan de oude regel (Valideren met het recht,
 * geen matrix), zodat de app niet leeg is tot de functie mee is.
 */
export function zichtUit(ik: IkAntwoord, bonnen: readonly Expense[]): Zicht {
  return ik.zicht ?? zichtVan(ik.ik.id, { alles: false, valideren: ik.mag.valideren }, bonnen)
}

export function bakkenVan(bonnen: Expense[], ik: IkAntwoord, nu = Date.now()): Bakken {
  const uit: Bakken = { mij: [], valideren: [], exact: [], zonderNaam: [], dispuut: [] }
  const z = zichtUit(ik, bonnen)
  const wie = ik.ik.id
  const inst = ik.instellingen
  for (const b of bonnen) {
    if (b.hoortBij || geenFactuur(b)) continue
    if (inDispuut(b)) {
      if (magZien(b, z, wie, inst, nu)) uit.dispuut.push(b)
      continue
    }
    if (wachtOpMij(b, z, wie, inst, nu)) {
      if (VALIDEER_STANDEN.includes(standVan(b, nu))) uit.valideren.push(b)
      else uit.mij.push(b)
      continue
    }
    const stand = standVan(b, nu)
    if (stand === 'boeken' || stand === 'geweigerd') {
      if (z.alles || teBoekenVoorMij(b, z, nu)) uit.exact.push(b)
      continue
    }
    /* Een handtekening zonder naam: in het To-do-overzicht van Alles zien, nergens anders. */
    if (z.alles && pastBijOverzicht(b, 'todo', nu)) {
      const stap = volgendeStap(b, regelsVoor(inst, b.amountExcl))
      if (stap && stap.stap !== 'valideren' && stap.groep.length === 0) uit.zonderNaam.push(b)
    }
  }
  uit.mij.sort(nieuwsteEerst)
  uit.valideren.sort(nieuwsteEerst)
  uit.exact.sort(nieuwsteEerst)
  uit.zonderNaam.sort(nieuwsteEerst)
  uit.dispuut.sort(nieuwsteEerst)
  return uit
}

/**
 * Na tekenen, afkeuren of terugzetten: door naar de volgende die op JOU
 * wacht -- volgendeVoorMij in TW10 Online (stukjes.tsx, Detail.tsx).
 *
 * Casper (22 okt 2026): "als je klaar met een factuur bent, en je
 * doorgestuurd wordt naar de ander, dat dit ook enkel naar die van
 * jouwzelf gebeurt." Eerst hetzelfde soort werk (wie net valideerde, de
 * volgende te valideren; anders de volgende handtekening), verder in de
 * lijst waar je vandaan kwam en dan de oudste; is dat op, de rest van je
 * eigen stapel. Wat de AI-assistent nog leest, is nog niet klaar om op te
 * pakken. Niets meer: null, en dan het feest (Klaar.tsx).
 */
export function volgendeVoorMij(naId: string, wasValideren: boolean, bonnen: Expense[], ik: IkAntwoord,
  lijst: string[], nu = Date.now()): Expense | null {
  const z = zichtUit(ik, bonnen)
  const alle = bonnen.filter((e) => e.id !== naId && !e.hoortBij && !geenFactuur(e))
  const teValideren = (e: Expense) => ['valideren', 'aanvullen'].includes(standVan(e, nu))
  const klaarVoorMij = (e: Expense) => standVan(e, nu) !== 'lezen' && wachtOpMij(e, z, ik.ik.id, ik.instellingen, nu)
  const zelfdeSoort = (e: Expense) => (wasValideren && z.valideren ? teValideren(e) : !teValideren(e) && klaarVoorMij(e))
  const i = lijst.indexOf(naId)
  const volgorde = i >= 0 ? [...lijst.slice(i + 1), ...lijst.slice(0, i)] : lijst
  const opId = new Map(alle.map((e) => [e.id, e]))
  for (const past of [zelfdeSoort, klaarVoorMij]) {
    for (const id of volgorde) {
      const e = opId.get(id)
      if (e && past(e)) return e
    }
    const oudste = alle.filter(past).sort((a, b) => (a.date ?? 0) - (b.date ?? 0))[0]
    if (oudste) return oudste
  }
  return null
}
